"use client";

import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import Image from 'next/image';
import { ArrowUp, ArrowLeft, ArrowRight, Camera, Move, MapPin, Loader2, RefreshCw, Bug } from "lucide-react";
import { metrics, trace, type Span } from '@opentelemetry/api';
import { logs } from '@opentelemetry/api-logs';

import { useVoyageStore } from "@/hooks/use-voyage-store";
import { LOCATION_PROXIMITY_THRESHOLD } from "@/lib/game-data";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import PhotoSuggestion from "./photo-suggestion";
import { useToast } from "@/hooks/use-toast";
import { generatePhoto } from "@/ai/flows/generate-photo-flow";
import { generateMapBackground } from "@/ai/flows/generate-map-background-flow";
import { generateCollisionImage } from "@/ai/flows/generate-collision-flow";
import type { HazardType } from "@/types";
import { HazardIcon, PlayerShipIcon } from "@/components/icons";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { generateNewVoyageData } from "@/ai/flows/generate-new-voyage-data-flow";

// The OTel SeverityNumber enum is not available in the client-side bundle.
// We define it locally to allow the code to compile.
enum SeverityNumber {
    INFO = 9,
    WARN = 13,
    ERROR = 17,
}


const getHazardAnimation = (type: HazardType) => {
    switch (type) {
        case 'shark':
            return 'animate-shark-swim';
        case 'whirlpool':
            return 'animate-spin';
        case 'kraken':
        case 'buoy':
        case 'ship':
            return 'animate-ship-bob';
        case 'rocks':
            return ''; // Rocks don't move
        default:
            return '';
    }
}

export default function GameClient() {
  const { regionName, shipPosition, shipRotation, moveForward, turnShip, addPhoto, photos, isInitialized, mapBackgroundUrl, setMapBackgroundUrl, collision, clearCollision, locations, hazards, startNewVoyage } = useVoyageStore();
  const { toast } = useToast();
  const [isTakingPhoto, setIsTakingPhoto] = useState(false);
  const [isGeneratingMap, setIsGeneratingMap] = useState(false);
  const [isGeneratingCollision, setIsGeneratingCollision] = useState(false);
  const [collisionImage, setCollisionImage] = useState<string | null>(null);
  const [collisionAudio, setCollisionAudio] = useState<string | null>(null);
  const [isGeneratingNewMap, setIsGeneratingNewMap] = useState(false);
  const prevActiveLocationId = useRef<string | null>(null);
  const voyageSpanRef = useRef<Span | null>(null);
  const tracer = useMemo(() => trace.getTracer('konstantins-voyage-client'), []);
  
  const [telemetry, setTelemetry] = useState<any | null>(null);

  useEffect(() => {
    const meter = metrics.getMeter('konstantins-voyage');
    const logger = logs.getLogger('konstantins-voyage');
    const photoCounter = meter.createCounter('voyage.photos.taken', {
        description: 'Counts the number of photos taken at points of interest.',
    });
    const collisionCounter = meter.createCounter('voyage.collisions', {
        description: 'Counts the number of collisions with hazards.',
    });
    const poiVisitedCounter = meter.createCounter('voyage.poi.visited', {
        description: 'Counts the number of unique points of interest visited.',
    });
    setTelemetry({ logger, photoCounter, collisionCounter, poiVisitedCounter });
  }, []);

  useEffect(() => {
    // Clean up the main voyage span on component unmount
    return () => {
      if (voyageSpanRef.current) {
        voyageSpanRef.current.end();
      }
    };
  }, []);


  useEffect(() => {
    const getMapBackground = async () => {
        if (!mapBackgroundUrl && regionName) {
            setIsGeneratingMap(true);
            telemetry?.logger.emit({
                severityNumber: SeverityNumber.INFO,
                body: `Generating new map background for ${regionName}.`,
                attributes: { 'region.name': regionName },
            });
            try {
                const result = await generateMapBackground({ regionName });
                if (result.imageUrl) {
                    setMapBackgroundUrl(result.imageUrl);
                    telemetry?.logger.emit({
                        severityNumber: SeverityNumber.INFO,
                        body: 'Map background generated successfully.',
                        attributes: { 'region.name': regionName },
                    });
                } else {
                    throw new Error("Map generation failed to return a URL.");
                }
            } catch (error) {
                const err = error as Error;
                console.error("Error generating map background:", err);
                telemetry?.logger.emit({
                    severityNumber: SeverityNumber.ERROR,
                    body: `Map background generation failed: ${err.message}`,
                    attributes: {
                        'region.name': regionName,
                        'error.stack': err.stack,
                    },
                });
                toast({
                    variant: 'destructive',
                    title: "Map Generation Failed",
                    description: "Could not generate the voyage map. Using a fallback.",
                });
            } finally {
                setIsGeneratingMap(false);
            }
        }
    };

    if (isInitialized) {
        getMapBackground();
    }
  }, [isInitialized, mapBackgroundUrl, regionName, setMapBackgroundUrl, toast, telemetry]);

  const activeLocation = useMemo(() => {
    for (const loc of locations) {
      const distance = Math.sqrt(
        Math.pow(shipPosition.x - loc.coords.x, 2) +
        Math.pow(shipPosition.y - loc.coords.y, 2)
      );
      if (distance < LOCATION_PROXIMITY_THRESHOLD) {
        return loc;
      }
    }
    return null;
  }, [shipPosition, locations]);
  
  const hasPhotoOfLocation = useMemo(() => {
    if (!activeLocation) return false;
    return photos.some(p => p.locationId === activeLocation.id);
  }, [photos, activeLocation]);

  const timeOfDay = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'morning';
    if (hour < 18) return 'afternoon';
    return 'evening';
  }, []);

  // Metric for visiting a new POI
  useEffect(() => {
    if (activeLocation && activeLocation.id !== prevActiveLocationId.current) {
        telemetry?.poiVisitedCounter.add(1, { 'location.id': activeLocation.id, 'location.name': activeLocation.name });
        telemetry?.logger.emit({
            severityNumber: SeverityNumber.INFO,
            body: `Visited new POI: ${activeLocation.name}`,
            attributes: {
                'location.id': activeLocation.id,
                'location.name': activeLocation.name,
            },
        });
        prevActiveLocationId.current = activeLocation.id;
    } else if (!activeLocation && prevActiveLocationId.current !== null) {
        // Reset when leaving a POI, allowing it to be counted again if re-visited.
        prevActiveLocationId.current = null;
    }
  }, [activeLocation, telemetry]);

  const handleTakePhoto = useCallback(async () => {
    if (activeLocation && !hasPhotoOfLocation && !isTakingPhoto) {
      await tracer.startActiveSpan('take-photo', async (span) => {
        span.setAttributes({
            'location.id': activeLocation.id,
            'location.name': activeLocation.name,
        });

        setIsTakingPhoto(true);
        telemetry?.logger.emit({
            severityNumber: SeverityNumber.INFO,
            body: `Attempting to take photo of ${activeLocation.name}`,
            attributes: { 'location.id': activeLocation.id, 'location.name': activeLocation.name },
        });
        try {
            const result = await generatePhoto({
                locationName: activeLocation.name,
                description: activeLocation.description,
                timeOfDay: timeOfDay,
            });

            if (result.imageUrl) {
                addPhoto(activeLocation, result.imageUrl);
                telemetry?.photoCounter.add(1, { 'location.id': activeLocation.id, 'location.name': activeLocation.name });
                telemetry?.logger.emit({
                    severityNumber: SeverityNumber.INFO,
                    body: `Successfully captured photo of ${activeLocation.name}`,
                    attributes: { 'location.id': activeLocation.id, 'location.name': activeLocation.name },
                });
                toast({
                    title: "Photo Captured!",
                    description: `You've taken a beautiful photo of ${activeLocation.name}.`,
                });
                span.setStatus({ code: 1 }); // OK
            } else {
                throw new Error("Image generation failed to return a URL.");
            }
        } catch (error) {
            const err = error as Error;
            console.error("Error generating photo:", err);
            span.recordException(err);
            span.setStatus({ code: 2, message: err.message }); // ERROR
            telemetry?.logger.emit({
                severityNumber: SeverityNumber.ERROR,
                body: `Photo generation failed: ${err.message}`,
                attributes: {
                    'location.id': activeLocation?.id,
                    'location.name': activeLocation?.name,
                    'error.stack': err.stack,
                },
            });
            toast({
                variant: 'destructive',
                title: "Photo Generation Failed",
                description: "Could not generate a photo. Please try again later.",
            });
        } finally {
            setIsTakingPhoto(false);
            span.end();
        }
      });
    }
  }, [activeLocation, hasPhotoOfLocation, isTakingPhoto, timeOfDay, addPhoto, toast, telemetry, tracer]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (['ArrowUp', 'ArrowLeft', 'ArrowRight', ' '].includes(e.key)) {
        e.preventDefault();
      }

      switch (e.key) {
        case 'ArrowUp':
          moveForward();
          break;
        case 'ArrowLeft':
          turnShip('left');
          break;
        case 'ArrowRight':
          turnShip('right');
          break;
        case ' ':
            handleTakePhoto();
            break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [moveForward, turnShip, handleTakePhoto]);

  const handleCloseCollisionDialog = useCallback(() => {
    setCollisionImage(null);
    setCollisionAudio(null);
    clearCollision();
  }, [clearCollision]);

  useEffect(() => {
    const handleCollision = async () => {
        if (collision) {
            await tracer.startActiveSpan('handle-collision', async (span) => {
                span.setAttributes({
                    'hazard.type': collision.type,
                    'hazard.name': collision.name,
                });

                telemetry?.collisionCounter.add(1, { 'hazard.type': collision.type, 'hazard.name': collision.name });
                telemetry?.logger.emit({
                    severityNumber: SeverityNumber.WARN,
                    body: `Ship collided with hazard: ${collision.name}`,
                    attributes: {
                        'hazard.type': collision.type,
                        'hazard.name': collision.name,
                    },
                });
                setIsGeneratingCollision(true);
                setCollisionImage(null);
                setCollisionAudio(null);
                try {
                    const result = await generateCollisionImage({
                        hazardName: collision.name,
                        locationName: activeLocation?.name || "the open seas",
                    });
                    if (result.imageUrl && result.audioUrl) {
                        setCollisionImage(result.imageUrl);
                        setCollisionAudio(result.audioUrl);
                        telemetry?.logger.emit({
                            severityNumber: SeverityNumber.INFO,
                            body: 'Collision media generated successfully.',
                            attributes: { 'hazard.name': collision.name },
                        });
                        span.setStatus({ code: 1 }); // OK
                    } else {
                        throw new Error("Collision media generation failed to return a URL.");
                    }
                } catch (error) {
                    const err = error as Error;
                    console.error("Error generating collision media:", err);
                    span.recordException(err);
                    span.setStatus({ code: 2, message: err.message }); // ERROR
                    telemetry?.logger.emit({
                        severityNumber: SeverityNumber.ERROR,
                        body: `Collision media generation failed: ${err.message}`,
                        attributes: {
                            'hazard.name': collision?.name,
                            'error.stack': err.stack,
                        },
                    });
                    toast({
                        variant: 'destructive',
                        title: "Media Generation Failed",
                        description: "Could not generate media for the encounter.",
                    });
                    handleCloseCollisionDialog();
                } finally {
                    setIsGeneratingCollision(false);
                    span.end();
                }
            });
        }
    };

    handleCollision();
  }, [collision, telemetry, activeLocation, toast, handleCloseCollisionDialog, tracer]);


  const handleNewMap = async () => {
      // End the previous voyage span if it exists
      if (voyageSpanRef.current) {
          voyageSpanRef.current.end();
          voyageSpanRef.current = null;
      }

      await tracer.startActiveSpan('generate-new-voyage', async (span) => {
        setIsGeneratingNewMap(true);
        telemetry?.logger.emit({
          severityNumber: SeverityNumber.INFO,
          body: 'User requested a new map.',
        });
        toast({
            title: "Charting a New Course...",
            description: "Generating a new voyage. This may take a moment.",
        });
        try {
            const voyageData = await generateNewVoyageData({});
            const mapData = await generateMapBackground({ regionName: voyageData.regionName });

            startNewVoyage({
                ...voyageData,
                mapBackgroundUrl: mapData.imageUrl,
            });

            // Start the new main voyage span
            voyageSpanRef.current = tracer.startSpan('voyage');
            voyageSpanRef.current.setAttributes({ 
                'region.name': voyageData.regionName,
                'app.session.id': 'konstantin-' + Date.now(),
            });

            telemetry?.logger.emit({
              severityNumber: SeverityNumber.INFO,
              body: `New voyage to ${voyageData.regionName} generated.`,
              attributes: { 'region.name': voyageData.regionName },
            });

            toast({
                title: "New Voyage Ready!",
                description: `Welcome to ${voyageData.regionName}. Your adventure awaits.`,
            });
            span.setStatus({ code: 1 }); // OK
        } catch (error) {
            const err = error as Error;
            console.error("Error generating new voyage:", err);
            span.recordException(err);
            span.setStatus({ code: 2, message: err.message }); // ERROR
            telemetry?.logger.emit({
              severityNumber: SeverityNumber.ERROR,
              body: `Failed to generate new voyage: ${err.message}`,
              attributes: { 'error.stack': err.stack },
            });
            toast({
                variant: 'destructive',
                title: "Failed to Generate New Map",
                description: "Could not create a new voyage. Please try again.",
            });
        } finally {
            setIsGeneratingNewMap(false);
            span.end();
        }
      });
  };

  const isCollisionDialogOpen = isGeneratingCollision || !!collisionImage;

  if (!isInitialized) {
    return (
        <div className="absolute inset-0 bg-background flex items-center justify-center">
            <Loader2 className="h-16 w-16 animate-spin text-primary" />
        </div>
    );
  }

  return (
    <>
      <div className="absolute inset-0 overflow-hidden">
        <TooltipProvider>
            {/* Map Area */}
            <div className="absolute inset-0 bg-gradient-to-b from-blue-200 to-primary/80">
                {isGeneratingMap && !mapBackgroundUrl && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-background/80 z-20 backdrop-blur-sm">
                      <Loader2 className="h-12 w-12 animate-spin text-primary" />
                      <p className="mt-4 text-primary font-semibold font-headline">Generating Your Custom Map...</p>
                      <p className="text-muted-foreground text-sm">This may take a moment.</p>
                  </div>
                )}
                {mapBackgroundUrl ? (
                  <Image src={mapBackgroundUrl} alt={`Map of ${regionName}`} fill className="object-cover" data-ai-hint="map New England coast"/>
                ) : (
                  <div className="w-full h-full bg-gradient-to-b from-blue-200 to-primary/80" />
                )}
                
                {locations.map(loc => (
                    <Tooltip key={loc.id}>
                        <TooltipTrigger asChild>
                            <button
                                className="absolute"
                                style={{ left: `${loc.coords.x}%`, top: `${loc.coords.y}%`, transform: 'translate(-50%, -50%)' }}
                            >
                                <MapPin className={`w-6 h-6 transition-all duration-300 ${activeLocation?.id === loc.id ? 'text-accent scale-150' : 'text-white/70 hover:text-white'}`}/>
                            </button>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p className="font-headline">{loc.name}</p>
                        </TooltipContent>
                    </Tooltip>
                ))}

                {hazards.map((hazard, index) => (
                    <Tooltip key={hazard.id}>
                        <TooltipTrigger asChild>
                            <button
                                className="absolute"
                                style={{
                                    left: `${hazard.coords.x}%`,
                                    top: `${hazard.coords.y}%`,
                                    transform: 'translate(-50%, -50%)'
                                }}
                            >
                                <HazardIcon
                                    type={hazard.type}
                                    className={`w-5 h-5 text-accent drop-shadow-md ${getHazardAnimation(hazard.type)}`}
                                    style={{ animationDelay: `${index * 250}ms`}}
                                />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p className="font-headline">{hazard.name}</p>
                        </TooltipContent>
                    </Tooltip>
                ))}

                <div
                    className="absolute transition-all duration-1000 ease-in-out"
                    style={{
                        left: `${shipPosition.x}%`,
                        top: `${shipPosition.y}%`,
                        transform: `translate(-50%, -50%) rotate(${shipRotation}deg)`,
                    }}
                >
                    <PlayerShipIcon className="w-10 h-10 fill-primary text-primary drop-shadow-lg" />
                </div>
            </div>

            {/* UI Overlay */}
            <div className="absolute top-4 right-4 z-10 w-full max-w-sm space-y-4">
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="font-headline text-2xl flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 truncate">
                        <span className="truncate">{activeLocation ? activeLocation.name : "Open Seas"}</span>
                    </div>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button variant="ghost" size="icon" className="flex-shrink-0" onClick={handleNewMap} disabled={isGeneratingNewMap || isGeneratingMap}>
                                {isGeneratingNewMap || (isGeneratingMap && !mapBackgroundUrl) ? <Loader2 className="h-5 w-5 animate-spin"/> : <RefreshCw className="h-5 w-5"/>}
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>Generate a New Map</p>
                        </TooltipContent>
                    </Tooltip>
                  </CardTitle>
                  <CardDescription>
                      {activeLocation ? `You've arrived at a point of interest in ${regionName}.` : "Navigate using the controls to find a location."}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground min-h-[60px]">
                      {activeLocation ? activeLocation.description : "The water is calm and the sky is clear. A perfect day for sailing. Explore the coast to find interesting sights."}
                  </p>
                  <div className="grid grid-cols-3 gap-2 justify-items-center pt-6">
                      <div></div>
                      <Button size="lg" variant="outline" onClick={moveForward} aria-label="Sail Forward"><ArrowUp /></Button>
                      <div></div>
                      <Button size="lg" variant="outline" onClick={() => turnShip('left')} aria-label="Turn Left"><ArrowLeft /></Button>
                      <div />
                      <Button size="lg" variant="outline" onClick={() => turnShip('right')} aria-label="Turn Right"><ArrowRight /></Button>
                  </div>
                </CardContent>
                <CardFooter className="flex flex-col gap-2">
                  <Button size="lg" className="w-full" onClick={handleTakePhoto} disabled={!activeLocation || hasPhotoOfLocation || isTakingPhoto}>
                    {isTakingPhoto ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin"/>
                        Generating Photo...
                      </>
                    ) : (
                      <>
                        <Camera className="mr-2 h-5 w-5"/>
                        {activeLocation ? (hasPhotoOfLocation ? 'Photo Already Taken' : `Photograph ${activeLocation.name}`) : 'No Location to Photograph'}
                      </>
                    )}
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => {
                      throw new Error("This is a test client-side error for testing telemetry.");
                    }}
                  >
                    <Bug className="mr-2 h-4 w-4" />
                    Throw Client Error
                  </Button>
                </CardFooter>
              </Card>
              
              {activeLocation && <PhotoSuggestion location={activeLocation} />}
            </div>

            {/* Controls Watermark */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10">
              <div className="flex items-center gap-6 bg-black/50 text-white/90 px-4 py-2 rounded-full shadow-lg backdrop-blur-sm text-sm font-body">
                <div className="flex items-center gap-2">
                  <Move className="w-4 h-4" />
                  <span>Arrow Keys</span>
                </div>
                <div className="flex items-center gap-2">
                  <Camera className="w-4 h-4" />
                  <span>Spacebar</span>
                </div>
              </div>
            </div>
        </TooltipProvider>
      </div>
      <AlertDialog open={isCollisionDialogOpen} onOpenChange={(open) => !open && handleCloseCollisionDialog()}>
          <AlertDialogContent>
              <AlertDialogHeader>
                  <AlertDialogTitle>A Perilous Encounter!</AlertDialogTitle>
                  <AlertDialogDescription>
                      {isGeneratingCollision 
                          ? `You've had a close call with a ${collision?.name}. Generating a depiction of the event...`
                          : `You've survived a close call with a ${collision?.name}!`}
                  </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="flex flex-col items-center justify-center min-h-[250px] sm:min-h-[400px] rounded-md bg-muted/50 space-y-4 p-4">
                  {isGeneratingCollision ? (
                      <Loader2 className="h-10 w-10 animate-spin text-primary" />
                  ) : collisionImage ? (
                      <>
                        <Image src={collisionImage} alt={`Encounter with ${collision?.name}`} width={400} height={300} className="rounded-md object-contain" />
                        {collisionAudio && (
                            <audio src={collisionAudio} autoPlay controls className="w-full" />
                        )}
                      </>
                  ) : null}
              </div>
              <AlertDialogFooter>
                  <AlertDialogAction onClick={handleCloseCollisionDialog} disabled={isGeneratingCollision}>
                      Continue Voyage
                  </AlertDialogAction>
              </AlertDialogFooter>
          </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
